import { Show } from "solid-js";
import { createRunOutput } from "./playgroundQueries";
import { RunOutputPanel } from "./RunOutputPanel";

/** Two finished runs, aligned column-by-column, so a prompt or catalog change
 * shows its effect on scores and findings. Column B carries a delta chip
 * against column A's agent score. */
export function RunCompare(props: {
  websiteId: string;
  slotA: string | null;
  slotB: string | null;
}) {
  const a = createRunOutput(
    () => props.websiteId,
    () => props.slotA,
  );
  const b = createRunOutput(
    () => props.websiteId,
    () => props.slotB,
  );
  const baseline = () => a.data?.run.agentScore ?? null;

  return (
    <div class="compare-grid">
      <CompareColumn slot="A" query={a} />
      <CompareColumn slot="B" query={b} baseline={baseline()} />
    </div>
  );
}

type OutputQuery = ReturnType<typeof createRunOutput>;

function CompareColumn(props: {
  slot: string;
  query: OutputQuery;
  baseline?: number | null;
}) {
  return (
    <section class="panel compare-col">
      <Show
        when={props.query.data}
        fallback={
          <div class="compare-empty">
            {props.query.isFetching
              ? "Loading run…"
              : `Pick a run for slot ${props.slot}`}
          </div>
        }
      >
        {(data) => (
          <>
            <header class="compare-col-head">
              <span class="compare-slot-tag">SLOT {props.slot}</span>
              <span class="mono" style={{ "font-size": "13px" }}>
                {data().url}
              </span>
              <span
                style={{ display: "flex", gap: "6px", "flex-wrap": "wrap" }}
              >
                <span class="badge">
                  {data().run.promptBundleLabel ?? "active prompt"}
                </span>
                <span class="badge">
                  {data().run.catalogDraftLabel ?? "canonical"}
                </span>
              </span>
              <span
                style={{
                  display: "flex",
                  gap: "12px",
                  "align-items": "baseline",
                }}
              >
                <span>agent {data().run.agentScore ?? "—"}</span>
                <span>human {data().run.humanScore ?? "—"}</span>
                <Delta
                  score={data().run.agentScore}
                  baseline={props.baseline}
                />
              </span>
            </header>
            <RunOutputPanel data={data()} runId={data().run.runId} />
          </>
        )}
      </Show>
    </section>
  );
}

function Delta(props: { score: number | null; baseline?: number | null }) {
  const diff = () =>
    props.baseline == null || props.score == null
      ? null
      : props.score - props.baseline;
  return (
    <Show when={diff() !== null && diff() !== 0}>
      {(_) => {
        const value = diff() as number;
        return (
          <span class={`score-delta ${value > 0 ? "up" : "down"}`}>
            {value > 0 ? "▲" : "▼"} {Math.abs(value)}
          </span>
        );
      }}
    </Show>
  );
}
